import { useState, useEffect, useRef } from "react";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { User, LogOut, ChevronDown, BookImage } from "lucide-react";
import { useNavigate } from "react-router-dom";

function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');

        if (token && userData) {
            setUser(JSON.parse(userData));
            setIsAuthenticated(true);
        }
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleNavigate = (path) => {
        console.log('Navigate to:', path);
        setIsMenuOpen(false);
        setIsProfileOpen(false);
        navigate(path);
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setUser(null);
        setIsAuthenticated(false);
        setIsProfileOpen(false);
        console.log('User logged out');
        navigate('/');
    };

    const menuItems = [
        { name: "Home", path: "/" },
        { name: "Trips", path: "/trips" },
        { name: "Blog", path: "/blog" },
        { name: "Contact", path: "/contact" },
        { name: "About", path: "/about" },
    ];

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-[100] border bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        {/* Logo */}
                        <div
                            className="cursor-pointer flex-shrink-0"
                            onClick={() => navigate("/")}
                        >
                            <div className="h-16 md:h-20 flex items-center">
                                <img src="assets/BoomOnT.png" alt="logo" className=" h-20 mt-2 md:mt-1 md:h-22" />
                            </div>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-8">
                            {menuItems.map((item) => (
                                <button
                                    key={item.name}
                                    onClick={() => handleNavigate(item.path)}
                                    className="text-gray-700 font-medium text-base
                                               transition-colors duration-300
                                               hover:text-indigo-600"
                                >
                                    {item.name}
                                </button>
                            ))}
                        </nav>

                        {/* Desktop Auth Section */}
                        <div className="hidden lg:block">
                            {isAuthenticated ? (
                                <div className="relative z-[101]" ref={dropdownRef}>
                                    <button
                                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                                        className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200
                                                   px-4 py-2.5 rounded-full font-medium text-sm
                                                   transition-all duration-300"
                                    >
                                        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                                            <User className="text-white" size={18} />
                                        </div>
                                        <span className="text-gray-700">
                                            {user?.name || user?.email?.split('@')[0] || user?.phone || 'User'}
                                        </span>
                                        <ChevronDown
                                            size={16}
                                            className={`text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''
                                                }`}
                                        />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {isProfileOpen && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg 
                                                    border border-gray-200 py-2 animate-fadeIn">
                                            {/* User Info */}
                                            <div className="px-4 py-3 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {user?.name || 'User'}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1 truncate">
                                                    {user?.email || user?.phone}
                                                </p>
                                            </div>

                                            {/* Menu Items */}
                                            <button
                                                onClick={() => handleNavigate('/profile')}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700
                                                       hover:bg-gray-50 transition-colors flex items-center gap-3"
                                            >
                                                <User size={16} className="text-gray-500" />
                                                View Profile
                                            </button>
                                            <button
                                                onClick={() => handleNavigate('/bookinghistory')}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700
                                                       hover:bg-gray-50 transition-colors flex items-center gap-3"
                                            >
                                                <BookImage size={16} className="text-gray-500" />
                                                Booking history
                                            </button>

                                            <button
                                                onClick={handleLogout}
                                                className="w-full text-left px-4 py-2.5 text-sm text-red-600
                                                       hover:bg-red-50 transition-colors flex items-center gap-3
                                                       border-t border-gray-200 mt-1"
                                            >
                                                <LogOut size={16} className="text-red-600" />
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    onClick={() => navigate("/login")}
                                    className="bg-gray-900 hover:bg-gray-800
                                               text-white px-6 py-2.5 rounded-full font-medium
                                               text-sm transition-all duration-300 transform hover:scale-105"
                                >
                                    Login now
                                </button>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="lg:hidden text-gray-700 hover:text-indigo-600 transition-colors"
                            aria-label="Toggle menu"
                        >
                            {isMenuOpen ? (
                                <HiX className="h-6 w-6" />
                            ) : (
                                <HiMenuAlt3 className="h-6 w-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="lg:hidden fixed top-16 md:top-20 left-0 right-0
                                bg-white shadow-lg z-40 border-b border-gray-200
                                max-h-[calc(100vh-4rem)] overflow-y-auto"
                >
                    <nav className="py-4">
                        {/* User Info in Mobile */}
                        {isAuthenticated && (
                            <div className="px-6 py-3 border-b border-gray-200 mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                                        <User className="text-white" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">
                                            {user?.name || 'User'}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {user?.email || user?.phone}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Items */}
                        {menuItems.map((item) => (
                            <button
                                key={item.name}
                                onClick={() => handleNavigate(item.path)}
                                className="w-full text-left px-6 py-3
                                           text-gray-700 font-medium
                                           transition-colors duration-300
                                           hover:bg-gray-50 hover:text-indigo-600"
                            >
                                {item.name}
                            </button>
                        ))}

                        {/* Mobile Auth Buttons */}
                        <div className="px-6 py-4 border-t border-gray-200 mt-2">
                            {isAuthenticated ? (
                                <div className="space-y-2">
                                    <button
                                        onClick={() => handleNavigate('/profile')}
                                        className="w-full bg-gray-100 hover:bg-gray-200
                                                   text-gray-900 px-6 py-3 rounded-full
                                                   font-medium text-sm transition-colors
                                                   flex items-center justify-center gap-2"
                                    >
                                        <User size={16} />
                                        View Profile
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full bg-red-50 hover:bg-red-100
                                                   text-red-600 px-6 py-3 rounded-full
                                                   font-medium text-sm transition-colors
                                                   flex items-center justify-center gap-2"
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleNavigate("/login")}
                                    className="w-full bg-gray-900 hover:bg-gray-800
                                               text-white px-6 py-3 rounded-full
                                               font-medium text-sm transition-colors"
                                >
                                    Login now
                                </button>
                            )}
                        </div>
                    </nav>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </>
    );
}

export default Header;